import React from 'react';
import { Trash2 } from 'lucide-react';
import { Lead, Child } from '../../../types/lead.types';
import { format } from 'date-fns';
import { useProjectSegments } from '../../../hooks/useProjectSegments';
import { SchoolSegmentSelect } from '../SchoolSegmentSelect';

const LEAD_STATUSES = ['uncontacted', 'cold', 'warm', 'hot', 'enrolled', 'lost', 'dnc'] as const;

interface LeadInfoTabProps {
  editedLead: Lead;
  setEditedLead: React.Dispatch<React.SetStateAction<Lead>>;
  isEditing: boolean;
  capturedByName: string;
}

const LeadInfoTab: React.FC<LeadInfoTabProps> = ({
  editedLead,
  setEditedLead,
  isEditing,
  capturedByName
}) => {
  const { segments } = useProjectSegments(editedLead.projectId);
  const segmentName = (id?: string) => segments.find(s => s.id === id)?.name;

  const updateChild = (index: number, patch: Partial<Child>) => {
    setEditedLead({
      ...editedLead,
      children: (editedLead.children || []).map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });
  };

  return (
    <div className="space-y-6">
      {/* Parent Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Parent Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">Parent Name</label>
            <input
              type="text"
              value={editedLead.parentName || ''}
              onChange={(e) => setEditedLead({ ...editedLead, parentName: e.target.value })}
              disabled={!isEditing}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={editedLead.email || ''}
              onChange={(e) => setEditedLead({ ...editedLead, email: e.target.value })}
              disabled={!isEditing}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Phone</label>
            <input
              type="tel"
              value={editedLead.phone || ''}
              onChange={(e) => setEditedLead({ ...editedLead, phone: e.target.value })}
              disabled={!isEditing}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Preferred Language</label>
            <select
              value={editedLead.language || ''}
              onChange={(e) => setEditedLead({ ...editedLead, language: e.target.value })}
              disabled={!isEditing}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
            >
              <option value="">Not specified</option>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>
        </div>
      </div>

      {/* Address */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Address</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Street Address</label>
            <input
              type="text"
              value={editedLead.address || ''}
              onChange={(e) => setEditedLead({ ...editedLead, address: e.target.value })}
              disabled={!isEditing}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">City</label>
              <input
                type="text"
                value={editedLead.city || ''}
                onChange={(e) => setEditedLead({ ...editedLead, city: e.target.value })}
                disabled={!isEditing}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">State</label>
              <input
                type="text"
                value={editedLead.state || ''}
                onChange={(e) => setEditedLead({ ...editedLead, state: e.target.value })}
                disabled={!isEditing}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">ZIP Code</label>
              <input
                type="text"
                value={editedLead.zipCode || ''}
                onChange={(e) => setEditedLead({ ...editedLead, zipCode: e.target.value })}
                disabled={!isEditing}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Children */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Children</h3>
        {editedLead.children && editedLead.children.length > 0 ? (
          <div className="space-y-3">
            {editedLead.children.map((child, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg relative">
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => {
                      const name = child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim() || 'this child';
                      if (!window.confirm(`Remove ${name} from this lead?`)) return;
                      setEditedLead({
                        ...editedLead,
                        children: (editedLead.children || []).filter((_, i) => i !== index)
                      });
                    }}
                    className="absolute top-2 right-2 p-1.5 text-red-500 hover:bg-red-50 rounded-md"
                    title="Remove child"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="mt-1 text-sm text-gray-900">{child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <p className="mt-1 text-sm text-gray-900">{child.dateOfBirth}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Grade</label>
                    <p className="mt-1 text-sm text-gray-900">{child.grade}</p>
                  </div>
                  {segments.length > 0 && child.schoolSegmentId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Campus</label>
                      <p className="mt-1 text-sm text-gray-900">{segmentName(child.schoolSegmentId) || '—'}</p>
                    </div>
                  )}
                </div>

                {/* Per-student status + billing determination */}
                <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    {isEditing ? (
                      <select
                        value={child.status || ''}
                        onChange={(e) => updateChild(index, { status: (e.target.value || undefined) as Child['status'] })}
                        className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">—</option>
                        {LEAD_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    ) : (
                      <p className="mt-1 text-sm text-gray-900 capitalize">{child.status || '—'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Determination (billing)</label>
                    {isEditing ? (
                      <select
                        value={child.determination || ''}
                        onChange={(e) => updateChild(index, { determination: (e.target.value || undefined) as Child['determination'] })}
                        className="mt-1 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">—</option>
                        <option value="pending">Pending</option>
                        <option value="valid">Valid</option>
                        <option value="invalid">Invalid</option>
                      </select>
                    ) : (
                      <p className="mt-1 text-sm text-gray-900 capitalize">{child.determination || '—'}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No children information</p>
        )}
      </div>

      {/* Lead Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Lead Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Source</label>
            <input
              type="text"
              value={editedLead.source || ''}
              onChange={(e) => setEditedLead({ ...editedLead, source: e.target.value })}
              disabled={!isEditing}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
            />
          </div>
          {segments.length > 0 && (
            <div>
              {isEditing ? (
                <SchoolSegmentSelect
                  segments={segments}
                  value={editedLead.schoolSegmentId}
                  onChange={(id) => setEditedLead({
                    ...editedLead,
                    schoolSegmentId: id,
                    schoolSegmentName: segmentName(id) || ''
                  })}
                  label="School / Campus"
                  emptyLabel="No school selected"
                />
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700">School / Campus</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {segmentName(editedLead.schoolSegmentId) || editedLead.schoolSegmentName || '—'}
                  </p>
                </>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">🚗 Ride provided</label>
            {isEditing ? (
              <label className="mt-1 flex items-center gap-2 text-sm text-gray-900">
                <input
                  type="checkbox"
                  checked={!!editedLead.rideProvided}
                  onChange={(e) => setEditedLead({ ...editedLead, rideProvided: e.target.checked })}
                />
                Field staff drives family to the tour
              </label>
            ) : (
              <p className="mt-1 text-sm text-gray-900">{editedLead.rideProvided ? 'Yes' : 'No'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Captured By</label>
            <p className="mt-1 text-sm text-gray-900">{capturedByName || 'Unknown'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Created Date</label>
            <p className="mt-1 text-sm text-gray-900">
              {editedLead.createdAt ? format(editedLead.createdAt.toDate(), 'PPP') : 'Unknown'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Last Updated</label>
            <p className="mt-1 text-sm text-gray-900">
              {editedLead.lastUpdated ? format(editedLead.lastUpdated.toDate(), 'PPP') : 'Unknown'}
            </p>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Notes</h3>
        <textarea
          value={editedLead.notes || ''}
          onChange={(e) => setEditedLead({ ...editedLead, notes: e.target.value })}
          disabled={!isEditing}
          rows={4}
          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
        />
      </div>
    </div>
  );
};

export default LeadInfoTab;
